import React, { useEffect, useMemo, useState } from 'react';
import {
  Smartphone, RefreshCw, Search, AlertCircle, Lock, RotateCcw, Eraser, Archive, X, ExternalLink,
} from 'lucide-react';
import { adminAPI } from '../../utils/api';

const MIRADORE_ENROLL_URL = 'https://online.miradore.com/yapo/EnrollDevice';

const COLUMNS = [
  { key: 'user', label: 'User' },
  { key: 'model', label: 'Model' },
  { key: 'platform', label: 'Platform' },
  { key: 'management', label: 'Enrollment' },
  { key: 'online', label: 'Online status' },
  { key: 'status', label: 'Status' },
  { key: 'lastReported', label: 'Last reported' },
];

// Destructive actions require a confirmation step.
const ACTIONS = {
  lock: { label: 'Lock', icon: Lock, destructive: false, className: 'text-gray-600 hover:bg-gray-50' },
  reboot: { label: 'Reboot', icon: RotateCcw, destructive: false, className: 'text-blue-600 hover:bg-blue-50' },
  wipe: { label: 'Wipe (factory reset)', icon: Eraser, destructive: true, className: 'text-red-600 hover:bg-red-50' },
  retire: { label: 'Retire (remove)', icon: Archive, destructive: true, className: 'text-amber-600 hover:bg-amber-50' },
};

const cell = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));

const statusPill = (value) => {
  const v = String(value || '').toLowerCase();
  const cls =
    v === 'active' ? 'bg-green-100 text-green-800'
      : v === 'deleted' || v === 'unmanaged' ? 'bg-gray-100 text-gray-600'
      : v === 'suspended' ? 'bg-amber-100 text-amber-800'
      : 'bg-blue-100 text-blue-800';
  return <span className={`px-2 py-1 rounded text-xs font-medium ${cls}`}>{cell(value)}</span>;
};

const Devices = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(null); // `${id}:${action}` currently running
  const [confirm, setConfirm] = useState(null); // { device, action }

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await adminAPI.getMiradoreDevices();
      setDevices(data.devices || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load devices from Miradore');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runAction = async (device, action) => {
    setBusy(`${device.id}:${action}`);
    setNotice('');
    setError('');
    try {
      await adminAPI.miradoreDeviceAction(device.id, action);
      setNotice(`${ACTIONS[action].label} sent to ${device.user || device.model || `device ${device.id}`}.`);
      setConfirm(null);
      // Give Miradore a moment, then refresh.
      setTimeout(load, 1200);
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action} the device`);
      setConfirm(null);
    } finally {
      setBusy(null);
    }
  };

  const onActionClick = (device, action) => {
    if (ACTIONS[action].destructive) {
      setConfirm({ device, action });
    } else {
      runAction(device, action);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return devices;
    return devices.filter((d) => Object.values(d).some((v) => String(v).toLowerCase().includes(q)));
  }, [devices, search]);

  return (
    <div className="p-8">
      <div className="bg-white border-b border-gray-200 px-8 py-6 mb-6 rounded-lg">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Smartphone className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Devices</h1>
              <p className="text-sm text-gray-600 mt-1">
                Android devices managed in Miradore — view status and run remote actions.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={MIRADORE_ENROLL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              <ExternalLink className="h-4 w-4" /> Enroll device
            </a>
            <button
              type="button"
              onClick={load}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search devices (user, model, status…)"
            className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {notice ? (
        <div className="mb-6 flex items-center justify-between rounded-lg border border-green-200 bg-green-50 px-5 py-3 text-sm text-green-700">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice('')} className="text-green-700 hover:text-green-900"><X className="h-4 w-4" /></button>
        </div>
      ) : null}

      {error ? (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <div>{error}</div>
        </div>
      ) : null}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          <p className="text-gray-600 mt-4">Loading devices from Miradore…</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Managed devices <span className="text-gray-500 font-normal">({filtered.length})</span>
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col.key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{col.label}</th>
                  ))}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length + 1} className="px-6 py-8 text-center text-gray-500">
                      No devices enrolled in Miradore yet.
                    </td>
                  </tr>
                ) : (
                  filtered.map((d, index) => {
                    const inactive = ['deleted', 'unmanaged'].includes(String(d.status).toLowerCase());
                    return (
                      <tr key={d.id || index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{cell(d.user)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{cell(d.model)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{cell(d.platform)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{cell(d.management)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{cell(d.online)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{statusPill(d.status)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{cell(d.lastReported)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-1.5">
                            {Object.entries(ACTIONS).map(([key, cfg]) => {
                              const Icon = cfg.icon;
                              const running = busy === `${d.id}:${key}`;
                              return (
                                <button
                                  key={key}
                                  type="button"
                                  title={inactive ? `${cfg.label} (device not active)` : cfg.label}
                                  disabled={inactive || Boolean(busy)}
                                  onClick={() => onActionClick(d, key)}
                                  className={`inline-flex items-center justify-center rounded-md border border-gray-200 p-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${cfg.className}`}
                                >
                                  {running ? <span className="block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <Icon className="h-4 w-4" />}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Confirmation for destructive actions */}
      {confirm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !busy && setConfirm(null)}>
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5">
              <h3 className="text-lg font-semibold text-gray-900">{ACTIONS[confirm.action].label}?</h3>
              <p className="mt-2 text-sm text-gray-600">
                {confirm.action === 'wipe'
                  ? 'This will factory-reset the device — all data is erased and it is removed from management. This cannot be undone.'
                  : 'This removes the device from Miradore management. You can re-enroll it later.'}
              </p>
              <p className="mt-2 text-sm font-medium text-gray-900">
                {confirm.device.user || confirm.device.model || `Device ${confirm.device.id}`}
              </p>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button type="button" onClick={() => setConfirm(null)} disabled={Boolean(busy)} className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button
                type="button"
                onClick={() => runAction(confirm.device, confirm.action)}
                disabled={Boolean(busy)}
                className={`rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${confirm.action === 'wipe' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`}
              >
                {busy ? 'Working…' : `Yes, ${ACTIONS[confirm.action].label.split(' ')[0].toLowerCase()}`}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Devices;
